import { useCallback, useEffect, useRef } from "react";

//—— 短音效 ——
//
//  用法：
//    const playCoin = useSound("/sounds/coin.mp3", { volume: 0.8, voices: 3 });
//    playCoin();
//
//  —— 為什麼不直接 new Audio().play() ——
//
//  1. **要預載**。第一次呼叫才去抓檔案的話，第一下一定是啞的 ——
//     而儀式的第一下正是最要緊的那一下。
//
//  2. **同一個元素沒辦法重疊播放**。三枚銅錢相隔 110ms 落地，
//     用同一個 <audio> 的話第二枚會把第一枚切斷，聽起來像只掉了一枚。
//     所以開一個小池子輪流用（voices）。
//
//  3. **失敗要安靜**。檔案還沒放進去（404）、瀏覽器擋自動播放、
//     使用者裝置沒有音訊輸出 —— 這些都不該讓畫面壞掉或噴紅字。
//     音效是加分的東西，不是功能。
//
//  —— 自動播放的限制 ——
//  瀏覽器只允許「使用者互動之後」播放有聲音的東西。這支 hook 的呼叫點
//  都在 pointer / click 事件裡（搖龜殼），所以不會被擋。
//  要在非互動的時機播（例如一進頁面就響），那就一定要先有過一次互動。
const useSound = (src, { volume = 1, voices = 1 } = {}) => {
  const poolRef = useRef([]);
  //輪到池子裡的第幾個。用 ref 不用 state —— 它只在播放時遞增，
  //  不需要觸發重畫。
  const nextRef = useRef(0);

  useEffect(() => {
    if (!src) return undefined;

    const pool = Array.from({ length: Math.max(1, voices) }, () => {
      const audio = new Audio(src);
      audio.preload = "auto";
      audio.volume = volume;
      return audio;
    });
    poolRef.current = pool;

    return () => {
      //  離場時停掉並鬆開檔案。不把 src 清空的話，有些瀏覽器
      //  會把已經卸載的元素連同緩衝一起留著。
      pool.forEach((audio) => {
        audio.pause();
        audio.src = "";
      });
      poolRef.current = [];
    };
  }, [src, volume, voices]);

  return useCallback(() => {
    const pool = poolRef.current;
    if (!pool.length) return;

    const audio = pool[nextRef.current % pool.length];
    nextRef.current += 1;

    try {
      //  倒帶再播：同一個聲音連續觸發時，不倒帶的話第二次不會發聲
      //  （它還停在上一次的結尾）。
      audio.currentTime = 0;
      //  play() 回 Promise，被擋或檔案不存在時會 reject ——
      //  一定要接住，未處理的 rejection 會在 console 噴紅字。
      audio.play().catch(() => {});
    } catch {
      //  currentTime 在還沒載入完成時可能丟 InvalidStateError。
      //  一樣吞掉 —— 少一聲音效不值得讓畫面出事。
    }
  }, []);
};

export default useSound;
